import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import AnnualMileageCalculator from '@acorn-portal/shared/src/components/calculators/AnnualMileageCalculator';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import MileageSlider from '@acorn-portal/shared/src/components/slider/MileageSlider';
import { Form, Formik } from 'formik';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, useTheme, Alert } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChangeOfMileageSchema from '@acorn-portal/shared/src/utils/validation/ChangeOfMileageSchema';
import { MileageTracker } from '@acorn-portal/shared/src/components/telematics/mileage';
import useTopUpMilesData from '@acorn-portal/shared/src/hooks/useTopUpMilesData';
import useMileageFormLogic from '@acorn-portal/shared/src/hooks/use-mileage-form-logic';
import { useMileageModalHelperText } from '../../hooks/useMileageModalHelperText';
import parser from 'html-react-parser';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

export interface ITopUpMilesForm {
	annualMileage: number;
}

const TopUpMiles = () => {
	const [modalOpen, setModalOpen] = useState(false);
	const navigate = useNavigate();
	const theme = useTheme();
	const { loading, midTermAdjustment, lists, setLoading } = useTopUpMilesData();
	const { discardLoading } = useDiscardMta();
	const { initialValues, onSubmit, isTelematics } = useMileageFormLogic(midTermAdjustment, lists, setLoading);
	const { track } = useTrack();
	// Get the minimum allowed mileage (current policy mileage)
	const minMileage = midTermAdjustment?.vehicleDetails?.annualMileage || 0;

	const mileageModalHelperText = useMileageModalHelperText();

	useEffect(() => {
		track(`Top Up Miles Page Viewed`);
	}, []);

	return (
		<JourneyCardLayout pageTitle='Top Up Your Mileage' loading={loading || discardLoading}>
			<Box maxWidth='md' margin='auto' sx={{ mb: { xs: 2, sm: 3 } }}>
				<Formik<ITopUpMilesForm>
					key={JSON.stringify(midTermAdjustment.transactionId)}
					validationSchema={ChangeOfMileageSchema(minMileage)}
					initialValues={initialValues}
					onSubmit={onSubmit}
					onReset={() => navigate(-1)}
				>
					{(formik) => (
						<Form>
							<Box maxWidth='sm' margin='auto'>
								<MileageTracker proposedMileage={formik.values.annualMileage} hideWarning hideTopUpButton />
							</Box>

							<Box maxWidth='sm' margin='auto' sx={{ mt: 2 }}>
								<Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
									Adjust your annual mileage:
								</Typography>
								<MileageSlider
									value={formik.values.annualMileage}
									onChange={(mileage) => {
										formik.setFieldValue('annualMileage', mileage);
									}}
									minMileage={minMileage}
								/>
							</Box>

							{formik.touched.annualMileage && formik.errors.annualMileage && (
								<Typography
									color='error'
									variant='caption'
									sx={{
										display: 'block',
										marginTop: 2,
										textAlign: 'center',
									}}
								>
									{formik.errors.annualMileage}
								</Typography>
							)}

							<Box
								sx={{
									padding: { xs: '10px 5px 20px', sm: '10px 10px 20px' },
									maxWidth: 'sm',
									margin: 'auto',
								}}
							>
								{/* Mileage comparison card */}
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										borderRadius: 1,
									}}
								>
									<Box>
										<Typography variant='caption' color='text.secondary'>
											Current mileage
										</Typography>
										<Typography variant='body1' fontWeight='500'>
											{minMileage.toLocaleString()} miles
										</Typography>
									</Box>

									<Box
										sx={{
											fontSize: 20,
											color: theme.palette.text.secondary,
											mx: 1,
										}}
									>
										→
									</Box>

									<Box sx={{ textAlign: 'right' }}>
										<Typography variant='caption' color='text.secondary'>
											New mileage
										</Typography>
										<Typography
											variant='body1'
											fontWeight='500'
											color='primary'
											sx={{
												alignItems: 'center',
											}}
										>
											{formik.values.annualMileage.toLocaleString()} miles
										</Typography>
									</Box>
								</Box>

								{/* Important disclaimers */}
								<Alert
									severity='info'
									icon={<InfoOutlinedIcon />}
									sx={{
										mt: 4,
										borderRadius: 1,
										'& .MuiAlert-message': {
											width: '100%',
										},
									}}
								>
									<Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
										Important Information
									</Typography>
									<Typography variant='body2'>{parser(mileageModalHelperText)}</Typography>
								</Alert>
							</Box>

							{isTelematics && (
								<Box
									sx={{
										marginBottom: 3,
										maxWidth: 'sm',
										margin: 'auto',
										padding: { xs: '10px 5px 30px', sm: '20px 10px 40px' },
									}}
								>
									<AnnualMileageCalculator miles={formik.values.annualMileage} id='annualMileageCalculator' />
								</Box>
							)}

							<ActionButtons>
								<BackButton onClick={() => setModalOpen(true)}>Back</BackButton>
								<ContinueButton type='submit'>Continue</ContinueButton>
							</ActionButtons>

							<HelperModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								title='Discard The Changes?'
								description='Are you sure you wish to discard the changes?'
								closeText='No'
								submitText='Yes'
								onSubmit={() => formik.resetForm()}
							/>
						</Form>
					)}
				</Formik>
			</Box>
		</JourneyCardLayout>
	);
};

export default TopUpMiles;
